import { Injectable } from '@angular/core';
import { ERROR_MESSAGES } from '../../../shared/constants';
import { NotificationService } from '../../../app/core/services/notification.service';

export interface ErrorInfo {
  status?: number;
  message: string;
  details?: unknown;
}

@Injectable({
  providedIn: 'root'
})
export class ErrorHandlerService {

  constructor(private notificationService: NotificationService) {}

  handleHttpError(error: { status?: number; error?: { detail?: unknown; message?: string; errors?: unknown[]; classificationCode?: string }; message?: string }): ErrorInfo {
    const status = error?.status;
    const errorResponse = error?.error;

    let message = this.extractErrorMessage({
      error: errorResponse ? {
        detail: errorResponse.detail as { message?: string } | undefined,
        message: errorResponse.message
      } : undefined,
      message: error?.message
    });
    message = this.normalizeParsingFailureMessage(message, status);

    switch (status) {
      case 403:
        // Prefere mensagem do backend; só usa hardcoded se backend não fornecer
        message = message || ERROR_MESSAGES.PERMISSAO_NEGADA;
        break;
      case 422:
        message = this.handleValidationError(error);
        break;
      case 500:
        // Prefere mensagem do backend; hardcoded apenas como fallback
        message = message || ERROR_MESSAGES.ERRO_SERVIDOR_PERIODOS;
        break;
      default:
        if (!message) {
          message = ERROR_MESSAGES.ERRO_GERAL;
        }
    }

    return {
      status,
      message,
      details: error
    };
  }

  private handleValidationError(error: { error?: { detail?: unknown; message?: string; errors?: unknown[]; classificationCode?: string }; message?: string }): string {
    const errorResponse = error?.error;

    if (!errorResponse) {
      return this.normalizeParsingFailureMessage(error?.message || 'Erro de validação');
    }

    // Prioridade 1: detalhes (array ou objeto) - formato comum da API
    if (errorResponse.detail) {
      if (Array.isArray(errorResponse.detail)) {
        return this.normalizeParsingFailureMessage(errorResponse.detail[0] || 'Erro de validação');
      } else if (typeof errorResponse.detail === 'object' && errorResponse.detail !== null) {
        const detailObj = errorResponse.detail as { message?: string; erro?: string };
        return this.normalizeParsingFailureMessage(detailObj.message || detailObj.erro || JSON.stringify(errorResponse.detail));
      } else {
        return this.normalizeParsingFailureMessage(String(errorResponse.detail));
      }
    }

    // Prioridade 2: Verifica array de erros
    if (Array.isArray(errorResponse.errors) && errorResponse.errors.length > 0) {
      const firstError = errorResponse.errors[0];
      return this.normalizeParsingFailureMessage(firstError.message || firstError.msg || firstError || 'Erro de validação');
    }

    // Prioridade 3: Verifica campos de mensagem diretos
    let message = errorResponse.message 
      || (errorResponse as { erro?: string }).erro 
      || (errorResponse as { error?: string }).error
      || error?.message
      || 'Erro de validação';

    // Prioridade 4: Se tem classificationCode, adiciona à mensagem
    if (errorResponse.classificationCode && message !== 'Erro de validação') {
      message = `${message} - ${errorResponse.classificationCode}`;
    }

    // Se a mensagem contém detalhes adicionais, extrair apenas a mensagem principal
    if (typeof message === 'object' && message !== null) {
      const messageObj = message as { message?: string; erro?: string };
      message = messageObj.message || messageObj.erro || JSON.stringify(message);
    }

    return this.normalizeParsingFailureMessage(message);
  }

  private extractErrorMessage(error: { error?: { detail?: { message?: string }; message?: string }; message?: string }): string {
    return error?.error?.detail?.message
      || error?.error?.message
      || error?.message
      || '';
  }

  /** Usar quando SKIP_ERROR_NOTIFICATION=true (interceptor global desativado) e o componente precisa notificar manualmente. */
  handleError(error: unknown, context?: string): void {
    const errorInfo = this.handleHttpError(error as { status?: number; error?: { detail?: unknown; message?: string; errors?: unknown[]; classificationCode?: string }; message?: string });

    const title = errorInfo.status === 403 ? 'Sem permissão'
      : errorInfo.status === 422 ? 'Dados inválidos'
      : errorInfo.status && errorInfo.status >= 500 ? 'Erro no servidor'
      : 'Erro';
    this.notificationService.error({ title, message: errorInfo.message });
  }

  /** Extrai mensagem de erro cobrindo os vários formatos de resposta da API */
  extractErrorMessageComplete(error: unknown): string {
    const defaultMessage = 'Erro desconhecido';
    
    if (!error || typeof error !== 'object') {
      return defaultMessage;
    }

    const errorObj = error as { 
      error?: { 
        detail?: unknown; 
        message?: string; 
        errors?: unknown[]; 
        classificationCode?: string;
        erro?: string;
      }; 
      message?: string;
      status?: number;
    };

    // Prioridade 1: detail (pode ser array, objeto ou string)
    if (errorObj.error?.detail) {
      if (Array.isArray(errorObj.error.detail)) {
        const firstDetail = errorObj.error.detail[0];
        if (typeof firstDetail === 'string') {
          return firstDetail;
        }
        if (typeof firstDetail === 'object' && firstDetail !== null) {
          return this.normalizeParsingFailureMessage((firstDetail as { message?: string; msg?: string; erro?: string }).message 
            || (firstDetail as { message?: string; msg?: string; erro?: string }).msg
            || (firstDetail as { message?: string; msg?: string; erro?: string }).erro
            || JSON.stringify(firstDetail));
        }
      } else if (typeof errorObj.error.detail === 'object') {
        const detailObj = errorObj.error.detail as Record<string, unknown> & { message?: string; erro?: string };
        const detailMessage = detailObj.message
          || detailObj.erro
          || this.extractFirstMeaningfulValue(detailObj)
          || JSON.stringify(errorObj.error.detail);
        return this.normalizeParsingFailureMessage(detailMessage, errorObj.status);
      } else if (typeof errorObj.error.detail === 'string') {
        return this.normalizeParsingFailureMessage(errorObj.error.detail, errorObj.status);
      }
    }

    // Prioridade 2: errors array
    if (errorObj.error && Array.isArray(errorObj.error.errors) && errorObj.error.errors.length > 0) {
      const firstError = errorObj.error.errors[0];
      if (typeof firstError === 'object' && firstError !== null) {
        return this.normalizeParsingFailureMessage((firstError as { message?: string; msg?: string }).message 
          || (firstError as { message?: string; msg?: string }).msg
          || String(firstError), errorObj.status);
      }
      return this.normalizeParsingFailureMessage(String(firstError), errorObj.status);
    }

    // Prioridade 3: message, erro, error
    if (errorObj.error?.message) return this.normalizeParsingFailureMessage(errorObj.error.message, errorObj.status);
    if (errorObj.error?.erro) return this.normalizeParsingFailureMessage(errorObj.error.erro, errorObj.status);
    if (errorObj.message) return this.normalizeParsingFailureMessage(errorObj.message, errorObj.status);

    // Prioridade 4: classificationCode
    if (errorObj.error?.classificationCode) {
      return errorObj.error.classificationCode;
    }

    return defaultMessage;
  }

  private extractFirstMeaningfulValue(value: unknown): string {
    if (typeof value === 'string') {
      return value;
    }

    if (Array.isArray(value)) {
      for (const item of value) {
        const extracted = this.extractFirstMeaningfulValue(item);
        if (extracted) {
          return extracted;
        }
      }

      return '';
    }

    if (!value || typeof value !== 'object') {
      return '';
    }

    for (const currentValue of Object.values(value as Record<string, unknown>)) {
      const extracted = this.extractFirstMeaningfulValue(currentValue);
      if (extracted) {
        return extracted;
      }
    }

    return '';
  }

  private normalizeParsingFailureMessage(message: unknown, status?: number): string {
    const text = String(message || '').trim();

    if (!text) {
      return '';
    }

    if (/Http failure (during parsing|response) for/i.test(text)) {
      if (status && status >= 400) {
        return 'Não foi possível concluir a operação no momento.';
      }

      return 'A operação foi concluída, mas a resposta do servidor não pôde ser processada. Atualize a tela para conferir os dados.';
    }

    return text;
  }
}